/**
 * Provider linking utilities for web
 * Opens streaming provider websites
 */

/**
 * Map of provider names to their canonical keys and URLs
 */
const PROVIDER_CONFIG = {
    netflix: {
        name: 'Netflix',
        baseUrl: 'https://www.netflix.com',
        searchUrl: (title) => `https://www.netflix.com/search?q=${encodeURIComponent(title)}`,
    },
    'amazon-prime-video': {
        name: 'Amazon Prime Video',
        baseUrl: 'https://www.primevideo.com',
        searchUrl: (title) => `https://www.primevideo.com/search?phrase=${encodeURIComponent(title)}`,
    },
    'disney-plus': {
        name: 'Disney Plus',
        baseUrl: 'https://www.disneyplus.com',
        searchUrl: (title) => `https://www.disneyplus.com/search?q=${encodeURIComponent(title)}`,
    },
    hulu: {
        name: 'Hulu',
        baseUrl: 'https://www.hulu.com',
        searchUrl: (title) => `https://www.hulu.com/search?q=${encodeURIComponent(title)}`,
    },
    'hbo-max': {
        name: 'HBO Max',
        baseUrl: 'https://www.max.com',
        searchUrl: (title) => `https://www.max.com/search?q=${encodeURIComponent(title)}`,
    },
    'apple-tv-plus': {
        name: 'Apple TV+',
        baseUrl: 'https://tv.apple.com',
        searchUrl: (title) => `https://tv.apple.com/search?term=${encodeURIComponent(title)}`,
    },
    paramount: {
        name: 'Paramount+',
        baseUrl: 'https://www.paramountplus.com',
        searchUrl: (title) => `https://www.paramountplus.com/search/?query=${encodeURIComponent(title)}`,
    },
    peacock: {
        name: 'Peacock',
        baseUrl: 'https://www.peacocktv.com',
        searchUrl: (title) => `https://www.peacocktv.com/search/${encodeURIComponent(title)}`,
    },
    'google-play-movies': {
        name: 'Google Play Movies',
        baseUrl: 'https://play.google.com/store/movies',
        searchUrl: (title) => `https://play.google.com/store/search?q=${encodeURIComponent(title)}&c=movies`,
    },
    'fandango-at-home': {
        name: 'Fandango at Home',
        baseUrl: 'https://www.fandangoathome.com',
        searchUrl: (title) => `https://www.fandangoathome.com/search/${encodeURIComponent(title)}`,
    },
    vudu: {
        name: 'Vudu',
        baseUrl: 'https://www.vudu.com',
        searchUrl: (title) => `https://www.vudu.com/content/movies/search?searchString=${encodeURIComponent(title)}`,
    },
    'microsoft-store': {
        name: 'Microsoft Store',
        baseUrl: 'https://www.microsoft.com/en-us/store/movies-and-tv',
        searchUrl: (title) => `https://www.microsoft.com/en-us/search/shop/movies?q=${encodeURIComponent(title)}`,
    },
    'youtube': {
        name: 'YouTube',
        baseUrl: 'https://www.youtube.com',
        searchUrl: (title) => `https://www.youtube.com/results?search_query=${encodeURIComponent(title)}+full+movie`,
    },
};

/**
 * Canonicalize provider name to a standard key
 */
export function canonicalizeProviderName(providerName) {
    if (!providerName) return null;

    const normalized = providerName.toLowerCase().trim();

    // Direct matches
    if (normalized.includes('netflix')) return 'netflix';
    if (normalized.includes('prime video') || normalized.includes('amazon')) return 'amazon-prime-video';
    if (normalized.includes('disney')) return 'disney-plus';
    if (normalized.includes('hulu')) return 'hulu';
    if (normalized.includes('hbo') || normalized.includes('max')) return 'hbo-max';
    if (normalized.includes('apple tv')) return 'apple-tv-plus';
    if (normalized.includes('paramount')) return 'paramount';
    if (normalized.includes('peacock')) return 'peacock';
    if (normalized.includes('google play')) return 'google-play-movies';
    if (normalized.includes('fandango')) return 'fandango-at-home';
    if (normalized.includes('vudu')) return 'vudu';
    if (normalized.includes('microsoft')) return 'microsoft-store';
    if (normalized.includes('youtube')) return 'youtube';

    return null;
}

/**
 * Get provider URL - either from backend link or fallback to search
 */
export function getProviderUrl(provider, mediaTitle) {
    // If backend provided a direct link, use it
    if (provider.link) {
        return provider.link;
    }

    // Otherwise, try to build a search URL
    const canonical = canonicalizeProviderName(provider.name);
    if (canonical && PROVIDER_CONFIG[canonical]) {
        return PROVIDER_CONFIG[canonical].searchUrl(mediaTitle);
    }

    // Final fallback - just open the provider's main site
    if (canonical && PROVIDER_CONFIG[canonical]) {
        return PROVIDER_CONFIG[canonical].baseUrl;
    }

    return null;
}

/**
 * Open a streaming provider in a new tab
 */
export function openProvider(provider, mediaTitle, analyticsCallback) {
    const url = getProviderUrl(provider, mediaTitle);

    if (!url) {
        console.warn('No URL available for provider:', provider.name);
        return;
    }

    // Track analytics if callback provided
    if (analyticsCallback) {
        analyticsCallback({
            provider: provider.name,
            type: provider.type,
            mediaTitle,
        });
    }

    // Open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
}